"use client"
import { Button, Divider, Typography } from "@mui/material"
import { CarModelStripDto, FutureCarsOrderType } from "api"
import { Desktop, Mobile } from "common/MediaQueries"
import { useInView } from "react-intersection-observer"
import BackButton from "reusable/BackButton"
import styled from "styled-components"
import ModelCard, { modelCardType } from "./ModelCard"

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(p) => p.theme.colors.lightGrey};
  ${(p) => p.theme.breakpoints.up("md")} {
    flex-direction: row;
  }
`
const StyledDivider = styled(Divider)`
  margin: 0 auto;
  width: calc(100% - 40px);
  background-color: ${(p) => p.theme.colors.darkGrey};
  height: 0.5px;
  ${(p) => p.theme.breakpoints.up("md")} {
    width: 100%;
    background-color: ${(p) => p.theme.colors.darkGrey};
    height: 1px;
  }
`
const StyledWhiteSection = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(p) => p.theme.colors.white};
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin: 0 10px;
  }
  ${(p) => p.theme.breakpoints.up("md")} {
    padding: 40px;
  }
  ${(p) => p.theme.breakpoints.up("xl")} {
    padding: 40px 80px;
  }
`
const StyledDesktopGreySection = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  padding: 40px 20px 40px 60px;
  margin-bottom: 106px;
  position: relative;
  ${(p) => p.theme.breakpoints.up("xl")} {
    padding: 40px 60px 40px 160px;
  }
`

const StyledCarsGrid = styled.div<{ $hasComingSoon: boolean }>`
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-top: 40px;
    display: grid;
    grid-gap: 22px;
    grid-template-columns: ${({ $hasComingSoon }) =>
      $hasComingSoon ? `repeat(3, 267px)` : `repeat(4, 267px)`};
    justify-content: center;
  }
  ${(p) => p.theme.breakpoints.up("xl")} {
    grid-template-columns: repeat(4, 267px);
    grid-gap: 60px;
  }
`

const StyledCarsGridWhite = styled.div`
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-top: 40px;
    display: grid;
    grid-gap: 22px;
    grid-template-columns: 1fr;
    justify-content: center;
  }
  ${(p) => p.theme.breakpoints.up("xl")} {
    grid-template-columns: 1fr;

    grid-gap: 60px;
  }
`

const StyledMobileBackWrapper = styled.div`
  padding: 7px 10px 5px;
`
const StyledComingSoonText = styled(Typography).attrs({
  variant: "h2",
})`
  text-align: center;
  font-size: 24px;
  color: ${(p) => p.theme.colors.mazdaRed};
  ${(p) => p.theme.breakpoints.down("sm")} {
    padding: 8px 0px;
  }
  ${(p) => p.theme.breakpoints.up("md")} {
    font-size: 28px;
    height: 45px;
  }
`

const StyledFinanceHeader = styled.h1`
  font-family: OENaPro;
  color: ${(p) => p.theme.colors.black};
  text-align: center;
  font-weight: 400;
  font-size: 24px;
  line-height: 140%;
  letter-spacing: 0.05em;
  margin: 0;

  ${(p) => p.theme.breakpoints.up("md")} {
    font-size: 28px;
    letter-spacing: 0.09em;
  }
`

const StyledFinanceHeaderSmall = styled(Typography).attrs({ variant: "h1" })`
  font-family: OENaPro;
  text-align: center;
  color: ${(p) => p.theme.colors.black};
  font-size: 18px;
  font-weight: 300;
  line-height: 150%;

  ${(p) => p.theme.breakpoints.up("md")} {
    font-size: 20px;
    line-height: 160%;
    letter-spacing: 0.03em;
  }
`

const StyledMazda = styled.span`
  font-family: MazdaType;
  font-size: 28px;
  font-weight: 400;
  ${(p) => p.theme.breakpoints.up("md")} {
    font-weight: 500;
    font-size: 32px;
  }
`

const StyledButton = styled(Button)`
  background: none;
  border: none;
  ${(p) => p.theme.breakpoints.up("md")} {
    position: absolute;
    top: 0;
  }
  &:hover {
    background: none;
    border: none;
  }
`

export type ModelsPageModel = CarModelStripDto & { discount?: number }
type ModelsPageProps = {
  modelsData: ModelsPageModel[]
  futureCarsOrderType?: FutureCarsOrderType
  type?: modelCardType
}

const ModelsPage = ({
  modelsData,
  futureCarsOrderType,
  type = "main",
}: ModelsPageProps) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
  })
  const isFinance = type === "finance"
  const isshop = type === "shop"
  const comingSoonModel = isFinance
    ? []
    : modelsData.filter((item) => {
        return item.carState === "Future"
      })

  const availableModels = isFinance
    ? modelsData
    : modelsData.filter((item) => {
        return item.carState !== "Future"
      })
  // PreSale first, then the rest in API order (CMS: /admin/carmodel/ordercarstrip)
  const normalModels = [
    ...availableModels.filter((item) => item.carState === "PreSale"),
    ...availableModels.filter((item) => item.carState !== "PreSale"),
  ]
  return (
    <StyledWrapper ref={ref}>
      {!!modelsData?.length && (
        <>
          <Mobile>
            <StyledMobileBackWrapper>
              <StyledButton>
                <BackButton />
              </StyledButton>
            </StyledMobileBackWrapper>
            {isFinance || isshop ? (
              <>
                <StyledFinanceHeaderSmall>
                  {isshop ? "התאימו אישית" : "קבלו הצעת מימון"}
                </StyledFinanceHeaderSmall>
                <StyledFinanceHeader>
                  בחרו דגם <StyledMazda>MAZDA</StyledMazda>
                </StyledFinanceHeader>
              </>
            ) : (
              <>
                <StyledFinanceHeaderSmall>
                  כל הדגמים שלנו
                </StyledFinanceHeaderSmall>
                <StyledFinanceHeader>
                  בחרו דגם <StyledMazda>MAZDA</StyledMazda>
                </StyledFinanceHeader>
              </>
            )}
          </Mobile>

          <Mobile>
            {!!comingSoonModel?.length &&
              futureCarsOrderType === "Start" &&
              !isFinance && (
                <StyledWhiteSection>
                  <StyledComingSoonText>COMING SOON</StyledComingSoonText>
                  <StyledDivider />
                  {comingSoonModel?.map((item, index) => (
                    <div key={item.id}>
                      <ModelCard modelData={item} />
                      {index !== comingSoonModel.length - 1 && (
                        <StyledDivider />
                      )}
                    </div>
                  ))}
                </StyledWhiteSection>
              )}

            {normalModels?.map((item, index) => (
              <div key={item.id}>
                <ModelCard inView={inView} modelData={item} type={type} />
                {index !== normalModels.length - 1 && <StyledDivider />}
              </div>
            ))}
            {!!comingSoonModel?.length && futureCarsOrderType === "End" && (
              <StyledWhiteSection>
                <StyledComingSoonText>COMING SOON</StyledComingSoonText>
                <StyledDivider />
                {comingSoonModel?.map((item, index) => (
                  <div key={item.id}>
                    <ModelCard inView={inView} modelData={item} />
                    {index !== comingSoonModel.length - 1 && <StyledDivider />}
                  </div>
                ))}
              </StyledWhiteSection>
            )}
          </Mobile>

          <Desktop>
            {!!comingSoonModel?.length && (
              <StyledWhiteSection>
                <StyledComingSoonText>COMING SOON</StyledComingSoonText>
                <StyledCarsGridWhite>
                  {comingSoonModel?.map((item) => (
                    <div key={item.id}>
                      <ModelCard
                        inView={inView}
                        modelData={item}
                        key={item.id}
                      />
                    </div>
                  ))}
                </StyledCarsGridWhite>
              </StyledWhiteSection>
            )}
            <StyledDesktopGreySection>
              <StyledButton>
                <BackButton />
              </StyledButton>
              {isFinance || isshop ? (
                <>
                  <StyledFinanceHeaderSmall>
                    {isshop ? "התאימו אישית" : "קבלו הצעת מימון"}
                  </StyledFinanceHeaderSmall>
                  <StyledFinanceHeader>
                    בחרו דגם <StyledMazda>MAZDA</StyledMazda>
                  </StyledFinanceHeader>
                </>
              ) : (
                <StyledFinanceHeader>
                  דגמי <StyledMazda>MAZDA</StyledMazda>
                </StyledFinanceHeader>
              )}

              <StyledCarsGrid $hasComingSoon={!!comingSoonModel?.length}>
                {normalModels?.map((item) => (
                  <ModelCard
                    inView={inView}
                    modelData={item}
                    key={item.id}
                    type={type}
                  />
                ))}
              </StyledCarsGrid>
            </StyledDesktopGreySection>
          </Desktop>
        </>
      )}
    </StyledWrapper>
  )
}

export default ModelsPage
